import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import { LEGACY_EDITOR_HANDLER_NAMES, LEGACY_MSG_EVENT_NAMES, type LegacyHandlerName } from '@/msgdeskRuntime/contracts/legacyEvents'
import { legacyOperationGate } from './legacyOperationGate'
import { msgObj } from './msgObj'
import {
  currentLegacyPagingMode,
  normalizeProjectionMode,
  syncAfterSemanticMove,
  syncCurrentLegacyProjection,
  syncLegacyModeProjection,
  syncLegacyPageObject,
} from './projectionSync'
import { treeStore } from './MsgTreeStore'
import type { SemanticMoveSyncMeta } from './types'

type SyncRunner = (fn: () => void) => void;
const runSyncDefault: SyncRunner = fn => fn();

export function bindDataTreeGlobalHooks(options: { sync?: SyncRunner } = {}) {
  const runtime = getMsgDeskRuntime();
  const runSync = options.sync || runSyncDefault;
  legacyOperationGate.install();

  const ingestLegacyDataHtml = (html: string, options: any) => {
    let ingested = false;
    runSync(() => {
      const trimmed = typeof html === 'string' ? html.trim() : '';
      if (trimmed.startsWith('{')) {
        try {
          const payload = JSON.parse(trimmed);
          ingested =
            treeStore.mutate.ingestPhpSubsModel(payload, options?.reason || 'react-subs-json') ||
            treeStore.mutate.ingestLegacyMsgesJsonPayload(payload, options?.reason || 'legacy-msges-json');
          return;
        } catch {
          ingested = false;
        }
      }
      // Stage 2c: subs-load responses are mirror-only (legacy's import reads
      // the mirrored cache; the model is the single parsed source). The full
      // parse remains for initial boot payloads only.
      ingested = options?.isInitial
        ? treeStore.mutate.ingestLegacyDataContainers(html, 'initial-subs-html')
        : treeStore.mutate.mirrorLegacySubsResponse(html, 'subs-html');
    });
    return ingested;
  };

  const ingestPhpSubsModel = (payload: any, options: any) => {
    let ingested = false;
    runSync(() => {
      ingested = treeStore.mutate.ingestPhpSubsModel(payload, options?.reason || 'react-subs-json');
    });
    return ingested;
  };

  runtime.setGlobal('__MSG_REACT_SUBS__', {
    ingestLegacyDataHtml,
    ingestPhpSubsModel,
    renderAnswerSubsHtml: ingestLegacyDataHtml,
    removeAnswerSubsSelector: (selector: string) => {
      runSync(() => {
        treeStore.removeAnswerSubsSelector(selector);
      });
      return true;
    },
  });

  // Expose write mutations through the host sync runner so legacy callers see
  // state updates synchronously (e.g. $rjq.addClass -> check DOM in same stack).
  const flush = <T extends (...args: any[]) => any>(fn: T): T => ((...args: any[]) => {
    let result: any;
    runSync(() => { result = fn(...args); });
    return result;
  }) as T;

  runtime.setGlobal('__MSG_REACT_SUBS_MUTATE__', {
    setClass:         flush(treeStore.mutate.setClass),
    setCss:           flush(treeStore.mutate.setCss),
    setAttr:          flush(treeStore.mutate.setAttr),
    removeAttr:       flush(treeStore.mutate.removeAttr),
    setText:          flush(treeStore.mutate.setText),
    setHtml:          flush(treeStore.mutate.setHtml),
    setVal:           flush(treeStore.mutate.setVal),
    setVisible:       flush(treeStore.mutate.setVisible),
    setOpacity:       flush(treeStore.mutate.setOpacity),
    setSubState:      flush(treeStore.mutate.setSubState),
    showArray:        flush(treeStore.mutate.showArray),
    setCurrentPage:   flush(treeStore.mutate.setCurrentPage),
    syncLegacyRuntime: flush(treeStore.mutate.syncLegacyRuntime),
    updateTemplateData: flush(treeStore.mutate.updateTemplateData),
    ingestLegacyDataContainers: flush(treeStore.mutate.ingestLegacyDataContainers),
    ingestLegacyMsgesJsonPayload: flush(treeStore.mutate.ingestLegacyMsgesJsonPayload),
    consumeLegacyDataContainer: flush(treeStore.mutate.consumeLegacyDataContainer),
    hasClass:   treeStore.mutate.hasClass,
    getCss:     treeStore.mutate.getCss,
    getAttr:    treeStore.mutate.getAttr,
    getText:    treeStore.mutate.getText,
    getVal:     treeStore.mutate.getVal,
    has:        treeStore.mutate.has,
  });

  runtime.setGlobal('__MSG_REACT_SUBS_COMMAND__', {
    normalizeLegacyRjqOp: msgObj.command.normalizeLegacyRjqOp,
    acceptLegacyRjqOp: msgObj.command.acceptLegacyRjqOp,
    resolveLegacyUiCommand: msgObj.command.resolveLegacyUiCommand,
    applyLegacyUiOp: flush(treeStore.command.applyLegacyUiOp),
    send: flush(msgObj.command.send),
    setActive: flush(msgObj.command.setActive),
    setChecked: flush(msgObj.command.setChecked),
    checkSub: msgObj.command.checkSub,
    checkVisibleSubs: msgObj.command.checkVisibleSubs,
    moveCheckedTo: msgObj.command.moveCheckedTo,
    moveCheckedToTrash: msgObj.command.moveCheckedToTrash,
    moveCheckedToDrafts: msgObj.command.moveCheckedToDrafts,
    moveToTrash: msgObj.command.moveToTrash,
    moveToDrafts: msgObj.command.moveToDrafts,
    restoreChecked: msgObj.command.restoreChecked,
    setVisible: flush(msgObj.command.setVisible),
    setCurrentPage: flush(msgObj.command.setCurrentPage),
    showArray: flush(msgObj.command.showArray),
    delete: flush(msgObj.command.delete),
    legacyEvent: flush(msgObj.command.legacyEvent),
  });

  runtime.setGlobal('__MSG_REACT_PROJECTION_SYNC__', {
    syncPage: (pageObj: any, reason = 'legacy-page') => {
      let result: ReturnType<typeof syncLegacyPageObject> = null;
      const mode = normalizeProjectionMode(pageObj?.this_mode?.() || currentLegacyPagingMode());
      try {
        runSync(() => {
          treeStore.mutate.setRenderMode(mode);
          result = syncLegacyPageObject(pageObj, { mode });
        });
        runtime.setGlobal('__MSG_LAST_PROJECTION_SYNC__', {
          reason,
          mode,
          results: result ? [result] : [],
          timestamp: Date.now(),
        });
      } finally {
        legacyOperationGate.noteProjectionSync(reason);
      }
      return result;
    },
    syncMode: (mode: string, reason = 'legacy-mode', updateRenderProjection = false) => {
      let result: ReturnType<typeof syncLegacyModeProjection> = [];
      try {
        runSync(() => {
          result = syncLegacyModeProjection(mode, reason, { updateRenderProjection });
        });
      } finally {
        legacyOperationGate.noteProjectionSync(reason);
      }
      return result;
    },
    syncAll: (reason = 'legacy-all') => {
      let result: ReturnType<typeof syncCurrentLegacyProjection> = [];
      try {
        runSync(() => {
          result = syncCurrentLegacyProjection(reason);
        });
      } finally {
        legacyOperationGate.noteProjectionSync(reason);
      }
      return result;
    },
    syncMove: (meta: SemanticMoveSyncMeta, reason = 'semantic-move') => {
      let result: ReturnType<typeof syncAfterSemanticMove> = [];
      try {
        runSync(() => {
          result = syncAfterSemanticMove(meta, reason);
        });
      } catch (error) {
        runtime.setGlobal('__MSG_LAST_MOVE_PROJECTION_SYNC_ERROR__', {
          reason,
          message: error instanceof Error ? error.message : String(error),
          timestamp: Date.now(),
        });
      } finally {
        runtime.deleteGlobal('__MSG_PENDING_SEMANTIC_MOVE_SYNC__');
        legacyOperationGate.noteProjectionSync(reason);
      }
      return result;
    },
    syncRuntime: (reason = 'legacy-runtime') => {
      let result = false;
      try {
        runSync(() => {
          result = treeStore.mutate.syncLegacyRuntime(reason);
        });
      } finally {
        legacyOperationGate.noteProjectionSync(reason);
      }
      return result;
    },
  });

  runtime.setGlobal('__MSG_RJQ_FACADE__', {
    normalize: msgObj.command.normalizeLegacyRjqOp,
    accept: msgObj.command.acceptLegacyRjqOp,
  });

  // The event bus is fed with runtime strings (legacy emits by name), so it
  // narrows against the generated handler-name union instead of trusting the
  // caller. Unknown names warned-and-noop'd before this guard too, just
  // deeper in callLegacyHandler.
  const legacyHandlerNames: ReadonlySet<string> = new Set<string>([
    ...LEGACY_MSG_EVENT_NAMES,
    ...LEGACY_EDITOR_HANDLER_NAMES,
  ]);
  runtime.installEventBus((eventName: string, ...args: any[]) => {
    if (!legacyHandlerNames.has(eventName)) {
      console.warn(`[eventBus] Unknown legacy event: ${eventName}`);
      return undefined;
    }
    return msgObj.command.legacyEvent(eventName as LegacyHandlerName, ...args);
  });

  runtime.setGlobal('__MSG_LEGACY_OPERATION_GATE__', msgObj.legacy);
}

bindDataTreeGlobalHooks();
